<template>
  <e-sidebar
    id="sidebar-form-add-product"
    :title="$t('Adicionar produtos')"
    :show="showSidebar"
    :fetching="fetching"
    :saving="saving"
    width="90%"
    :confirm-button-text="$t('Ok')"
    :hide-cancel-button="true"
    @save="onSaveItem"
    @hidden="hide"
  >
    <template #content>
      <b-card-actions
        action-refresh
        :show-loading="fetching || busy"
        @refresh="getSkus({ storeId })"
      >
        <div slot="title">
          <e-page-size-selector
            :per-page="paging.pageSize"
            @change="pageSizeChange"
          />
        </div>

        <e-filters
          :search-text="$t('Filtrar')"
          :searching="fetching"
          :title="$t('Filtrar Produtos')"
          class="mb-2 card-bordered"
          @search="filterData"
          @reset="resetFilters"
        >
          <b-row>
            <b-col md="2">
              <FormulateInput
                id="filter-skuId"
                v-model="filters.skuId"
                type="number"
                :label="$t('Código')"
                :placeholder="$t('Código')"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                id="filter-ean"
                v-model="filters.ean"
                type="number"
                :label="$t('EAN/GTIN')"
                :placeholder="$t('Código de barras')"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                id="filter-name"
                v-model="filters.name"
                :label="$t('Nome')"
                :placeholder="$t('Nome')"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                id="filter-maximumStock"
                v-model="filters.maximumStock"
                :label="$t('Estoque até')"
                :placeholder="$t('Ex: 10')"
                :instruction="
                  $t(
                    'Quando preenchido, o sistema só trará produtos que tenham no máximo o estoque informado neste campo'
                  )
                "
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                id="filter-belowMinimumStock"
                v-model="filters.belowMinimumStock"
                type="switch"
                :label="$t('Abaixo do mínimo')"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                id="filter-brand"
                v-model="filters.brandId"
                type="vue-select"
                :label="$t('Marca')"
                :placeholder="$t('Selecione uma marca')"
                :options="getComboBrands"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                id="filter-group"
                v-model="filters.groupId"
                type="vue-select"
                :label="$t('Grupo')"
                :placeholder="$t('Selecione um grupo')"
                :options="getComboGroups"
                :disabled="isGroupLockedByRequisition"
                :instruction="
                  isGroupLockedByRequisition
                    ? $t('A requisição está restrita a este grupo de produto')
                    : ''
                "
                @input="selectGroup"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                id="filter-subgroup"
                v-model="filters.subgroupId"
                type="vue-select"
                :label="$t('Subgrupo')"
                :placeholder="$t('Selecione um subgrupo')"
                :options="getComboSubgroups"
                :disabled="!filters.group"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                id="filter-department"
                v-model="filters.departmentId"
                type="vue-select"
                :label="$t('Departamento')"
                :placeholder="$t('Selecione um departamento')"
                :options="getComboDepartments"
              />
            </b-col>
          </b-row>
        </e-filters>

        <b-table
          ref="table-skus"
          show-empty
          striped
          responsive
          class="mb-1 bordered"
          :fields="fields"
          :items="skuSearchResults"
          no-local-sorting
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.sortDesc"
          @sort-changed="getSkus({ storeId })"
        >
          <template #cell(action)="row">
            <span>
              <e-grid-actions
                :show-update="false"
                :show-delete="false"
                :buttons="[
                  {
                    icon: 'plus',
                    variant: 'primary',
                    title: $t('Adicionar produto'),
                    event: 'product-add',
                  },
                ]"
                @product-add="addProduct(row)"
              />
            </span>
          </template>
          <template #cell(skuName)="{ item }">
            <e-product-router-link
              :sku-id="item.sku.id"
              :product-id="item.sku.productId"
              :product-name="item.sku.name"
              :ean="item.sku.ean"
            />
          </template>
        </b-table>

        <b-row>
          <b-col cols="6">
            <e-pagination-summary
              :current-page="paging.currentPage"
              :per-page="paging.pageSize"
              :total="paging.rowCount"
              :total-on-page="paging.rowsInCurrentPage"
            />
          </b-col>
          <b-col cols="6">
            <b-pagination
              v-model="paging.currentPage"
              align="right"
              :total-rows="paging.rowCount"
              :per-page="paging.pageSize"
              aria-controls="user-table"
              @change="pageChange"
            />
          </b-col>
        </b-row>
      </b-card-actions>
    </template>
  </e-sidebar>
</template>

<script>
import _ from 'lodash'
import { BRow, BCol, BTable, BPagination } from 'bootstrap-vue'
import ESidebar from '@/views/components/ESidebar.vue'
import {
  alerts,
  authorizationRules,
  discountTypes,
  formulateHelper,
  payBoxUtils,
  saleItemUtils,
} from '@/mixins'
import delegablePermissions from '@/utils/delegable-permissions'
import { getInitialSaleItem } from '@/mixins/utils/sale-item-utils'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  EPageSizeSelector,
  EFilters,
  EProductRouterLink,
  EPaginationSummary,
  EGridActions,
} from '@/views/components'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'

const getSidebarForm = () => ({
  quantity: 1,
  skuProduct: null,
  unitDiscountAuth: null,
  unitPriceChangeAuth: null,
  discountType: discountTypes.computed.discountTypesEnum().VALUE,
  unitDiscount: 0,
  manualUnitValue: null,
})

const getInitialInfoValues = () => ({
  productPrice: 0,
  subtotal: 0,
  total: 0,
  totalDiscount: 0,
  priceTableDiscountTotal: 0,
  stock: null,
  promotionId: null,
  promotionQuantityAvailable: 0,
  promotionQuantityUnlimited: false,
  priceRuleSelected: null,
})

export default {
  components: {
    BRow,
    BCol,
    ESidebar,
    EPageSizeSelector,
    BCardActions,
    EFilters,
    EProductRouterLink,
    EPaginationSummary,
    BTable,
    BPagination,
    EGridActions,
  },

  mixins: [discountTypes, formulateHelper, authorizationRules, alerts],

  props: {
    storeId: {
      type: [String, Number],
      required: true,
    },
    priceTableId: {
      type: [String, Number],
      default: 1,
    },
  },

  data() {
    return {
      showSidebar: false,
      fetching: false,
      saving: false,
      itemForm: getSidebarForm(),
      infoValues: getInitialInfoValues(),
      saleItem: getInitialSaleItem(),
      delegateUserData: null,
      dataByEan: null,
    }
  },

  computed: {
    ...mapState('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', [
      'filters',
      'skuSearchResults',
      'paging',
      'sorting',
      'busy',
      'purchaseRequisition',
    ]),
    ...mapGetters('pages/catalog/products', [
      'getComboDepartments',
      'getComboBrands',
      'getComboGroups',
      'getComboSubgroups',
    ]),
    /**
     * Quando a requisição possui um grupo de produto, o filtro fica travado nesse grupo
     * para que somente produtos dele possam ser incluídos.
     */
    isGroupLockedByRequisition() {
      return !!this.purchaseRequisition?.groupId
    },

    isDiscountPercent() {
      return this.itemForm.discountType === this.discountTypesEnum.PERCENTAGE
    },

    getDiscountSymbol() {
      return this.isDiscountPercent ? this.$t('%') : this.$t('R$')
    },

    getPromotionQuantityAvailable() {
      return this.infoValues.promotionQuantityAvailable <= 0
        ? this.$t('Esgotado')
        : this.infoValues.promotionQuantityAvailable || '-'
    },

    isEdit() {
      return !!this.saleItem.id || !!this.saleItem.localId
    },

    isKit() {
      return this.saleItem?.kitItems?.length > 0
    },

    unitDiscountPermission() {
      return delegablePermissions.ERP_ORDER_ITEM_DISCOUNT
    },

    unitPriceChangePermission() {
      return delegablePermissions.ERP_ORDER_ITEM_PRICE_CHANGE
    },

    hasPriceTableDiscount() {
      return payBoxUtils.canApplyPriceTableDiscount(this.saleItem.priceRuleSelected)
    },

    canEditUnitValue() {
      return this.$router.currentRoute.name.indexOf('quotation') !== 0 && !this.saleItem.isKit
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Data/Hora'),
          key: 'modifiedDate',
          class: 'text-center',
          thStyle: { width: '150px' },
          sortable: true,
          formatter: (value, index, item) =>
            this.$options.filters.datetime(value || item.createdDate),
        },
        {
          label: this.$t('Loja'),
          key: 'store',
          tdClass: 'text-left',
          thStyle: { width: '250px' },
          formatter: val => this.$options.filters.storeName(val),
        },
        {
          label: this.$t('Produto'),
          key: 'skuName',
          sortKey: 'sku.name',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Reservado'),
          key: 'reservation',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: value => this.$options.filters.number(value, 3, true),
        },
        {
          label: this.$t('Em estoque'),
          key: 'stock',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: value => this.$options.filters.number(value, 3, true),
        },
        {
          label: this.$t('Disponível'),
          key: 'available',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: (value, index, item) =>
            this.$options.filters.number(item.stock - (item.reservation || 0), 3, true),
        },
      ]
    },
  },

  mounted() {
    // this.resetFilters()
    if (this.getComboBrands.length === 0) {
      this.fetchProductCombos()
    }
  },

  methods: {
    ...mapActions('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', [
      'getSkus',
      'addRequisitonItem',
      'resetFilters',
      'setFilterValue',
      'setCurrentPage',
      'setPageSize',
    ]),
    ...mapActions('pages/catalog/products', ['setComboSubgroups', 'fetchProductCombos']),

    /**
     * Ao filtrar, volta para a primeira página para não consultar um índice
     * de página que não existe mais no novo resultado.
     */
    filterData() {
      this.setCurrentPage(1)
      this.getSkus({ storeId: this.storeId })
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getSkus({ storeId: this.storeId })
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getSkus({ storeId: this.storeId })
    },

    /**
     * Aplica o grupo da requisição ao filtro de produtos, garantindo que a listagem
     * traga somente produtos do grupo selecionado na requisição.
     */
    applyRequisitionGroupFilter() {
      const { groupId } = this.purchaseRequisition

      if (!groupId) {
        return
      }

      if (this.filters.groupId !== groupId) {
        this.setFilterValue({ key: 'groupId', value: groupId })
      }

      this.setComboSubgroups(groupId)
    },

    async show(item) {
      this.cleanSidebar()
      this.applyRequisitionGroupFilter()
      this.showSidebar = true

      if (item) {
        try {
          this.fetching = true
          await new Promise(resolve => setTimeout(() => resolve(), 200))

          this.itemForm = {
            ...getSidebarForm(),
            quantity: item.quantity,
            skuProduct: item.skuProduct,
            unitDiscountAuth: item.unitDiscountAuth,
            unitPriceChangeAuth: item.unitPriceChangeAuth,
            discountType: item.discountType || this.discountTypesEnum.VALUE,
            unitDiscount: item.unitDiscount,
          }
          this.saleItem = { ...item }

          if (item.priceRuleSelected?.type === payBoxUtils.priceRuleTypes.MANUAL_PRICE_INCREASE) {
            this.itemForm.manualUnitValue = item.priceRuleSelected.priceInfo?.unitValue
          }

          await this.fetchSkuStockInfo()
          this.updateInfos()
        } finally {
          this.fetching = false
        }
      }
    },

    hide() {
      this.cleanSidebar()
      this.showSidebar = false
    },

    addProduct(row) {
      const skuId = row.item.sku?.id
      const productExists = (this.purchaseRequisition?.items || []).some(
        item => item.sku?.id === skuId
      )

      if (productExists) {
        this.showWarning({ message: this.$t('Produto já está na requisição.') })
        return
      }

      this.addRequisitonItem(row.item)
      this.showSuccess({ message: this.$t('Produto adicionado com sucesso.') })
    },

    cleanSidebar() {
      this.itemForm = getSidebarForm()
      this.saleItem = getInitialSaleItem()
      this.infoValues = getInitialInfoValues()
    },

    async onSaveItem() {
      this.hide()
      // try {
      //   this.saving = true
      //   this.$refs.itemSidebarForm.showErrors()
      //   if (this.$refs.itemSidebarForm.hasErrors) {
      //     this.showInvalidDataMessage()
      //     return
      //   }

      //   const saleItemData = {
      //     ...this.saleItem,
      //     quantity: this.itemForm.quantity,
      //     unitDiscountAuth: this.itemForm.unitDiscountAuth,
      //     unitPriceChangeAuth: this.itemForm.unitPriceChangeAuth,
      //     discountType: this.itemForm.discountType,
      //     unitDiscount: this.itemForm.unitDiscount,
      //     manualUnitValue: this.itemForm.manualUnitValue,
      //   }

      //   if (this.isEdit) {
      //     this.$emit('update', saleItemData)
      //   } else {
      //     this.$emit('add', saleItemData)
      //   }

      //   this.hide()
      // } catch (error) {
      //   this.showError({ error })
      // } finally {
      //   this.saving = false
      // }
    },

    async fetchData(ean) {
      const data = await this.$http.get(`/api/sales/pay-box-sku/store/${this.storeId}/ean/${ean}`, {
        params: { priceTableId: this.priceTableId },
      })

      return data
    },

    async onSelectProduct(productSearched) {
      if (!productSearched) return
      try {
        this.saving = true

        if (!productSearched) return

        const { ean } = productSearched
        const { data } = await this.fetchData(ean)

        if (data?.stock <= 0) {
          await this.confirm({
            title: this.$t('Produto sem estoque'),
            text: this.$t(`Este produto não pode ser adicionado pois está sem estoque.`),
            cancelButtonText: this.$t('Continuar'),
            showCancelButton: true,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: true,
            focusCancel: true,
          })
          this.cleanSidebar()
          this.focusInput('#sale_product_sidebar-sku_product')
          return
        }

        if (data.promotionId && !data.promotionQuantityUnlimited) {
          const localPromotionQuantityAdded = (this.itemsAdded || [])
            .filter(
              itemAdded =>
                itemAdded.localId &&
                itemAdded.skuProduct.skuId === data.skuId &&
                itemAdded.skuProduct.kit === data.kit &&
                itemAdded.skuProduct.promotionId === data.promotionId
            )
            .reduce((total, item) => total + (item.quantity || 0), 0)

          data.promotionQuantityAvailable -= localPromotionQuantityAdded
          if (data.promotionQuantityAvailable - localPromotionQuantityAdded < 0) {
            data.promotionId = null
          }
        }

        this.saleItem = saleItemUtils.buildProductToSaleItem(data, {
          quantity: this.itemForm.quantity,
          unitDiscount: this.itemForm.unitDiscount,
          discountType: this.itemForm.discountType,
        })

        this.focusInput('#sale_product_sidebar-quantity')
        this.updateInfos()
      } catch (error) {
        this.itemForm.skuProduct = null
        this.saleItem = getInitialSaleItem()
        this.infoValues = getInitialInfoValues()
        this.showError({ error })
        this.focusInput('#sale_product_sidebar-sku_product', false)
      } finally {
        this.saving = false
      }
    },

    onPermissionGranted(delegationData) {
      if (!delegationData) {
        return
      }

      if (!this.itemForm.manualUnitValue) {
        this.itemForm.manualUnitValue = this.infoValues.productPrice
      }
    },

    // eslint-disable-next-line func-names
    updateInfos: _.debounce(function () {
      if (!this.saleItem?.skuProduct) return

      this.saleItem = saleItemUtils.updateSaleItemOrSaleKit(this.saleItem, {
        quantity: this.itemForm.quantity,
        unitDiscount: this.itemForm.unitDiscount,
        discountType: this.itemForm.discountType,
        manualUnitValue: this.itemForm.manualUnitValue,
      })

      const { priceRuleSelected } = this.saleItem

      const { stock, promotionQuantityAvailable, promotionQuantityUnlimited } =
        this.saleItem?.skuProduct || {}

      const promotionAvailable = this.saleItem.id
        ? (promotionQuantityAvailable ?? 0) + (this.saleItem.maxQuantity ?? 0)
        : promotionQuantityAvailable ?? 0

      const { priceInfo } = priceRuleSelected

      this.infoValues = {
        productPrice: priceInfo.unitValue || 0,
        stock: stock ?? null,

        promotionId: priceRuleSelected.promotion?.id,
        promotionQuantityAvailable: promotionAvailable,
        promotionQuantityUnlimited: promotionQuantityUnlimited ?? false,

        subtotal: priceInfo.localGrossValue,
        totalDiscount: priceInfo.localDiscountTotal,
        total: priceInfo.localNetValue,
        priceTableDiscountTotal: priceInfo.localPriceTableDiscountTotal,
        priceRuleSelected,
      }
    }, 400),

    async fetchSkuStockInfo() {
      const ean = this.saleItem?.skuProduct?.ean
      const { data } = await this.$http.get(
        `/api/sales/pay-box-sku/store/${this.storeId}/ean/${ean}`,
        { params: { priceTableId: this.priceTableId } }
      )

      this.saleItem.skuProduct.stock = data.stock
      this.saleItem.skuProduct.tieredPrices = data.tieredPrices

      if (this.saleItem.skuTieredPriceId) {
        this.saleItem.skuProduct.originalPrice = data.originalPrice
      }

      if (this.saleItem.id) {
        const isToRefreshPromotionData =
          this.saleItem.promotionId &&
          data.promotionId &&
          this.saleItem.promotionId === data.promotionId
        if (isToRefreshPromotionData) {
          const promotionData = {
            promotionId: data.promotionId,
            priceFrom: data.priceFrom,
            promotionQuantityAvailable: data.promotionQuantityAvailable,
            promotionQuantityUnlimited: data.promotionQuantityUnlimited,
          }

          this.saleItem.skuProduct = {
            ...this.saleItem.skuProduct,
            ...promotionData,
          }
        }
      }

      const { priceRules } = payBoxUtils.buildPriceRulesFromSaleItem(this.saleItem)
      this.saleItem.priceRules = priceRules

      this.updateInfos()
    },

    selectGroup(idValue) {
      this.filters.subgroup = null
      this.setComboSubgroups(idValue)
    },
  },
}
</script>

<style lang="scss" scoped>
.text-bold {
  font-weight: 800;
}
</style>
